import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, Building2, MessageSquare, Settings,
  LogOut, CheckCircle, XCircle, Clock, TrendingUp, Eye, Menu, Loader2, Star, Calendar, CreditCard
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../lib/api';
import StarRating from '../../components/StarRating';

const LOGO = "https://static.prod-images.emergentagent.com/jobs/6554b71a-e92e-45d6-a2ea-28b8d837c5dd/images/05ed22a83a162dbd4a68bdfa54453a7a8a9f16d8e3a633061831e14ecf8e4761.jpeg";

const TYPE_LABELS = {
  homestay: 'Homestay', hotel: 'Hotel', pg: 'PG', hostel: 'Hostel',
  farmhouse: 'Farmhouse', penthouse: 'Penthouse',
  function_hall: 'Function Hall', banquet_hall: 'Banquet Hall'
};

const Sidebar = ({ active, setActive, sidebarOpen, setSidebarOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = [
    { key: 'overview', icon: <LayoutDashboard size={18} />, label: 'Overview' },
    { key: 'partners', icon: <Users size={18} />, label: 'Partners' },
    { key: 'properties', icon: <Building2 size={18} />, label: 'Properties' },
    { key: 'users', icon: <Users size={18} />, label: 'All Users' },
    { key: 'enquiries', icon: <MessageSquare size={18} />, label: 'Enquiries' },
    { key: 'reviews', icon: <Star size={18} />, label: 'Reviews' },
    { key: 'bookings', icon: <Calendar size={18} />, label: 'Bookings' },
    { key: 'seed', icon: <Settings size={18} />, label: 'Setup / Seed' },
  ];
  return (
    <>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-navy-dark z-40 transition-transform duration-300 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b border-white/10">
          <img src={LOGO} alt="HYGAD" className="h-9 mb-1" />
          <p className="text-white/40 text-xs">Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(({ key, icon, label }) => (
            <button key={key} onClick={() => { setActive(key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 ${
                active === key ? 'bg-gold/20 text-gold' : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}>
              {icon} {label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="px-4 py-3 mb-2">
            <div className="text-white/80 text-sm font-medium truncate">{user?.name}</div>
            <div className="text-gold text-xs">Admin</div>
          </div>
          <button onClick={() => { logout(); navigate('/'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

const StatCard = ({ icon, label, value, color = 'gold' }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(10,22,40,0.06)' }}>
    <div className={`w-10 h-10 rounded-xl mb-4 flex items-center justify-center ${
      color === 'gold' ? 'bg-gold/10 text-gold' : color === 'green' ? 'bg-green-100 text-green-600' :
      color === 'red' ? 'bg-red-100 text-red-500' : 'bg-blue-100 text-blue-500'
    }`}>{icon}</div>
    <div className="text-2xl font-bold text-navy mb-0.5">{value ?? '—'}</div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seedResult, setSeedResult] = useState('');
  const [seedLoading, setSeedLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    if (user.role !== 'Admin') { navigate('/'); return; }
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, p, pr, u, e, rv] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getPartners(),
        adminAPI.getProperties(),
        adminAPI.getUsers(),
        adminAPI.getEnquiries(),
        adminAPI.getReviews()
      ]);
      setStats(s); setPartners(p); setProperties(pr); setUsers(u); setEnquiries(e); setReviews(rv);
      adminAPI.getBookings().then(setBookings).catch(console.error);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const approvePartner = async (id, approved) => {
    await adminAPI.updatePartner(id, { is_approved: approved });
    setPartners(p => p.map(x => x.id === id ? { ...x, is_approved: approved } : x));
    loadData();
  };

  const updatePropertyStatus = async (id, status) => {
    await adminAPI.updateProperty(id, { status });
    setProperties(p => p.map(x => x.id === id ? { ...x, status } : x));
    loadData();
  };

  const runSeed = async () => {
    setSeedLoading(true);
    try {
      const r = await adminAPI.seed();
      setSeedResult(`✓ ${r.message} — Login: ${r.admin_email} / ${r.admin_password}`);
      loadData();
    } catch (e) { setSeedResult(`Error: ${e.message}`); }
    finally { setSeedLoading(false); }
  };

  const filteredPartners = filterStatus === 'pending' ? partners.filter(p => !p.is_approved) :
    filterStatus === 'approved' ? partners.filter(p => p.is_approved) : partners;

  const filteredProperties = filterStatus ? properties.filter(p => p.status === filterStatus) : properties;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAF9] flex">
      <Sidebar active={active} setActive={setActive} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center gap-4 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-navy"><Menu size={22} /></button>
          <div>
            <h1 className="font-semibold text-navy text-lg capitalize">{active}</h1>
            <p className="text-gray-400 text-xs">HYGAD Admin Panel</p>
          </div>
        </div>

        <div className="p-6 lg:p-8">
          {/* Overview */}
          {active === 'overview' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon={<Users size={20} />} label="Total Users" value={stats?.total_users} color="blue" />
                <StatCard icon={<Users size={20} />} label="Partners" value={stats?.total_partners} />
                <StatCard icon={<Clock size={20} />} label="Pending Partners" value={stats?.pending_partners} color="red" />
                <StatCard icon={<Building2 size={20} />} label="Total Properties" value={stats?.total_properties} color="green" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={<Clock size={20} />} label="Pending Properties" value={stats?.pending_properties} color="red" />
                <StatCard icon={<CheckCircle size={20} />} label="Approved Properties" value={stats?.approved_properties} color="green" />
                <StatCard icon={<MessageSquare size={20} />} label="Total Enquiries" value={stats?.total_enquiries} color="blue" />
                <StatCard icon={<TrendingUp size={20} />} label="Payments Received" value={stats?.total_payments} />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 mt-4">
                <StatCard icon={<Star size={20} />} label="Total Reviews" value={stats?.total_reviews} color="gold" />
                <StatCard icon={<Clock size={20} />} label="Pending Reviews" value={stats?.pending_reviews} color="red" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 mt-4">
                <StatCard icon={<Calendar size={20} />} label="Total Bookings" value={stats?.total_bookings ?? bookings.length} color="blue" />
                <StatCard icon={<CreditCard size={20} />} label="Booking Revenue" value={`₹${(stats?.total_booking_revenue||0).toLocaleString('en-IN')}`} />
              </div>

              {stats?.pending_partners > 0 && (
                <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-amber-800">{stats.pending_partners} partner(s) awaiting approval</p>
                    <p className="text-amber-600 text-sm">Review and approve new partners to activate their listings.</p>
                  </div>
                  <button onClick={() => setActive('partners')}
                    className="bg-amber-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors">
                    Review
                  </button>
                </div>
              )}

              {stats?.pending_properties > 0 && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-2xl p-5 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-blue-800">{stats.pending_properties} property(ies) awaiting approval</p>
                    <p className="text-blue-600 text-sm">Review property listings submitted by partners.</p>
                  </div>
                  <button onClick={() => setActive('properties')}
                    className="bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-600 transition-colors">
                    Review
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* Partners */}
          {active === 'partners' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex gap-2 mb-6">
                {['', 'pending', 'approved'].map(s => (
                  <button key={s || 'all'} onClick={() => setFilterStatus(s)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${filterStatus === s ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-500'}`}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {filteredPartners.map(p => (
                  <div key={p.id} data-testid={`admin-partner-${p.id}`}
                    className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center text-white font-bold shrink-0">
                        {p.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-navy text-sm">{p.name}</h4>
                        <p className="text-gray-400 text-xs truncate">{p.email}</p>
                        <p className="text-gray-400 text-xs">{p.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-xs px-2.5 py-1 rounded-full ${p.is_approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {p.is_approved ? 'Approved' : 'Pending'}
                      </span>
                      {!p.is_approved ? (
                        <button onClick={() => approvePartner(p.id, true)} data-testid={`approve-partner-${p.id}`}
                          className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                          <CheckCircle size={12} /> Approve
                        </button>
                      ) : (
                        <button onClick={() => approvePartner(p.id, false)} data-testid={`reject-partner-${p.id}`}
                          className="bg-red-100 hover:bg-red-200 text-red-600 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                          <XCircle size={12} /> Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {filteredPartners.length === 0 && (
                  <div className="text-center py-16 text-gray-400">No partners found.</div>
                )}
              </div>
            </motion.div>
          )}

          {/* Properties */}
          {active === 'properties' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex gap-2 mb-6">
                {['', 'pending', 'approved', 'rejected'].map(s => (
                  <button key={s || 'all'} onClick={() => setFilterStatus(s)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${filterStatus === s ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-500'}`}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {filteredProperties.map(p => (
                  <div key={p.id} data-testid={`admin-property-${p.id}`}
                    className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
                    <div className="w-20 h-16 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-navy text-sm">{p.name}</h4>
                      <p className="text-gray-400 text-xs mt-0.5">{p.location?.city} · {TYPE_LABELS[p.type]} · Max {p.max_guests} guests</p>
                      <p className="text-gold text-xs font-medium mt-1">₹{p.price_min?.toLocaleString('en-IN')} – ₹{p.price_max?.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${
                        p.status === 'approved' ? 'bg-green-100 text-green-700' :
                        p.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
                      }`}>{p.status}</span>
                      {p.status === 'pending' && (
                        <>
                          <button onClick={() => updatePropertyStatus(p.id, 'approved')} data-testid={`approve-property-${p.id}`}
                            className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                            <CheckCircle size={12} /> Approve
                          </button>
                          <button onClick={() => updatePropertyStatus(p.id, 'rejected')} data-testid={`reject-property-${p.id}`}
                            className="bg-red-100 hover:bg-red-200 text-red-600 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                            <XCircle size={12} /> Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {filteredProperties.length === 0 && (
                  <div className="text-center py-16 text-gray-400">No properties found.</div>
                )}
              </div>
            </motion.div>
          )}

          {/* Users */}
          {active === 'users' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left px-5 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                        <th className="text-left px-5 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                        <th className="text-left px-5 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
                        <th className="text-left px-5 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="text-left px-5 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {users.map(u => (
                        <tr key={u.id} data-testid={`admin-user-${u.id}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {u.name?.[0]?.toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-navy">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-500">{u.email}</td>
                          <td className="px-5 py-4">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                              u.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                              u.role === 'Partner' ? 'bg-gold/10 text-gold-dark' : 'bg-blue-100 text-blue-600'
                            }`}>{u.role}</span>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`text-xs ${u.is_active ? 'text-green-600' : 'text-red-500'}`}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-xs text-gray-400">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {stats?.pending_reviews > 0 && (
                <div className="mt-4 bg-gold/10 border border-gold/20 rounded-2xl p-5 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-navy">{stats.pending_reviews} review(s) awaiting moderation</p>
                    <p className="text-gray-500 text-sm">Approve or reject guest reviews.</p>
                  </div>
                  <button onClick={() => setActive('reviews')}
                    className="bg-navy text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-navy-light transition-colors">
                    Moderate
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* Reviews */}
          {active === 'reviews' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex gap-2 mb-6">
                {['', 'pending', 'approved', 'rejected'].map(s => (
                  <button key={s || 'all'} onClick={() => setFilterStatus(s)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${filterStatus === s ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-500'}`}>
                    {s || 'All'}
                    {s === 'pending' && stats?.pending_reviews > 0 && (
                      <span className="ml-1.5 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full inline-flex items-center justify-center">{stats.pending_reviews}</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="space-y-4">
                {(filterStatus ? reviews.filter(r => r.status === filterStatus) : reviews).map(r => (
                  <div key={r.id} data-testid={`admin-review-${r.id}`}
                    className="bg-white rounded-2xl border border-gray-100 p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {r.customer_name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-navy text-sm">{r.customer_name}</p>
                            <p className="text-gold text-xs">{r.property_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-11">
                          <StarRating value={r.rating} readOnly size="sm" />
                          <span className="text-gray-400 text-xs">{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${
                          r.status === 'approved' ? 'bg-green-100 text-green-700' :
                          r.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
                        }`}>{r.status}</span>
                        {r.status === 'pending' && (
                          <>
                            <button
                              data-testid={`approve-review-${r.id}`}
                              onClick={async () => { await adminAPI.updateReview(r.id, { status: 'approved' }); loadData(); }}
                              className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                              <CheckCircle size={12} /> Approve
                            </button>
                            <button
                              data-testid={`reject-review-${r.id}`}
                              onClick={async () => { await adminAPI.updateReview(r.id, { status: 'rejected' }); loadData(); }}
                              className="bg-red-100 hover:bg-red-200 text-red-600 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                              <XCircle size={12} /> Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {r.title && <p className="font-medium text-navy text-sm mb-1 ml-11">{r.title}</p>}
                    <p className="text-gray-500 text-sm ml-11">{r.comment}</p>
                  </div>
                ))}
                {reviews.length === 0 && (
                  <div className="text-center py-16 text-gray-400">No reviews yet.</div>
                )}
              </div>
            </motion.div>
          )}

          {/* Enquiries */}
          {active === 'enquiries' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="space-y-3">
                {enquiries.map(e => (
                  <div key={e.id} className="bg-white rounded-2xl border border-gray-100 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-navy text-sm">{e.name}</h4>
                        <p className="text-gold text-xs">{e.property_name}</p>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full ${e.status === 'new' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                        {e.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-500">
                      <div><span className="block text-gray-400 mb-0.5">Email</span>{e.email}</div>
                      <div><span className="block text-gray-400 mb-0.5">Phone</span>{e.phone}</div>
                      <div><span className="block text-gray-400 mb-0.5">Guests</span>{e.guests}</div>
                      <div><span className="block text-gray-400 mb-0.5">Date</span>{new Date(e.created_at).toLocaleDateString('en-IN')}</div>
                    </div>
                    {e.message && <p className="text-gray-400 text-xs mt-3 bg-gray-50 p-3 rounded-lg">{e.message}</p>}
                  </div>
                ))}
                {enquiries.length === 0 && <div className="text-center py-16 text-gray-400">No enquiries yet.</div>}
              </div>
            </motion.div>
          )}

          {/* Bookings */}
          {active === 'bookings' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {/* Summary */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard icon={<Calendar size={20} />} label="Total" value={bookings.length} color="blue" />
                <StatCard icon={<CheckCircle size={20} />} label="Confirmed" value={bookings.filter(b=>b.status==='confirmed').length} color="green" />
                <StatCard icon={<Clock size={20} />} label="Pending" value={bookings.filter(b=>b.status==='pending').length} color="red" />
                <StatCard icon={<CreditCard size={20} />} label="Revenue" value={`₹${bookings.filter(b=>b.payment_status==='paid').reduce((s,b)=>s+(b.total||0),0).toLocaleString('en-IN')}`} />
              </div>
              {/* Filter tabs */}
              <div className="flex gap-2 mb-5">
                {['', 'pending', 'confirmed', 'cancelled'].map(s => (
                  <button key={s||'all'} onClick={() => setFilterStatus(s)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${filterStatus===s?'bg-navy text-white':'bg-white border border-gray-200 text-gray-500'}`}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {(filterStatus ? bookings.filter(b=>b.status===filterStatus) : bookings).map(b => (
                  <div key={b.id} data-testid={`admin-booking-${b.booking_id}`}
                    className="bg-white rounded-2xl border border-gray-100 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-navy text-sm">{b.customer_name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                            b.status==='confirmed'?'bg-green-100 text-green-700':
                            b.status==='cancelled'?'bg-red-100 text-red-600':'bg-amber-100 text-amber-700'
                          }`}>{b.status}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${b.payment_status==='paid'?'bg-blue-100 text-blue-600':'bg-gray-100 text-gray-500'}`}>{b.payment_status}</span>
                        </div>
                        <p className="text-gold text-xs font-mono">{b.booking_id}</p>
                        <p className="text-gray-400 text-xs">{b.property_name}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-navy text-lg">₹{(b.total||0).toLocaleString('en-IN')}</p>
                        <div className="flex gap-1 mt-1 justify-end">
                          {b.status !== 'confirmed' && (
                            <button onClick={async()=>{await adminAPI.updateBooking(b.booking_id,{status:'confirmed'});adminAPI.getBookings().then(setBookings);}}
                              className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-lg hover:bg-green-200 transition-colors">
                              Confirm
                            </button>
                          )}
                          {b.status !== 'cancelled' && (
                            <button onClick={async()=>{await adminAPI.updateBooking(b.booking_id,{status:'cancelled'});adminAPI.getBookings().then(setBookings);}}
                              className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-lg hover:bg-red-200 transition-colors">
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs text-gray-500">
                      <div><span className="block text-gray-400 mb-0.5">Type</span><span className="capitalize">{b.experience_type}</span></div>
                      <div><span className="block text-gray-400 mb-0.5">{b.experience_type==='stay'?'Check-in':'Event Date'}</span>{b.check_in||b.event_date||'—'}</div>
                      {b.experience_type==='stay' && <div><span className="block text-gray-400 mb-0.5">Check-out</span>{b.check_out||'—'}</div>}
                      <div><span className="block text-gray-400 mb-0.5">Guests</span>{b.guests}</div>
                      <div><span className="block text-gray-400 mb-0.5">Phone</span>{b.customer_phone}</div>
                    </div>
                  </div>
                ))}
                {bookings.length === 0 && <div className="text-center py-16 text-gray-400">No bookings yet.</div>}
              </div>
            </motion.div>
          )}

          {/* Seed */}
          {active === 'seed' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg">
              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <h3 className="font-serif text-2xl font-bold text-navy mb-3">Platform Setup</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Seed the database with the admin account and sample properties. Safe to run multiple times — only creates data if it doesn't exist.
                </p>
                <div className="bg-navy rounded-xl p-4 mb-6 font-mono text-xs text-green-400">
                  <p className="text-white/60 mb-1">Admin credentials after seed:</p>
                  <p>Email: admin@hygad.in</p>
                  <p>Password: Hygad@Admin2024</p>
                </div>
                <button onClick={runSeed} disabled={seedLoading} data-testid="seed-btn"
                  className="bg-navy text-white px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 hover:bg-navy-light transition-colors disabled:opacity-60">
                  {seedLoading ? <Loader2 size={16} className="animate-spin" /> : <Settings size={16} />}
                  {seedLoading ? 'Seeding...' : 'Run Seed'}
                </button>
                {seedResult && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700">
                    {seedResult}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
